import { useState } from "react";

import { AppShell } from "@/components/layout/AppShell";
import { MicSettings } from "@/components/room/MicSettings";
import { SoundSettings } from "@/components/room/SoundSettings";
import { Seo } from "@/components/Seo";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { TextField } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";
import { useSession } from "@/context/SessionContext";
import { changePassword, updateProfile } from "@/lib/api/auth";
import { formatDay } from "@/lib/format";
import { useI18n } from "@/lib/i18n";

export function SettingsPage() {
  const { user, setUser } = useSession();
  const toast = useToast();
  const { t, locale } = useI18n();

  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  if (user === null) return null;

  const saveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingProfile(true);
    try {
      setUser(await updateProfile({ displayName: displayName.trim() }));
      toast.show(t("auth.profileSaved"), "success");
    } catch (error) {
      toast.fail(error);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingPassword(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      toast.show(t("auth.passwordChanged"), "success");
    } catch (error) {
      toast.fail(error);
    } finally {
      setSavingPassword(false);
    }
  };

  const since = formatDay(user.createdAt, locale);

  return (
    <AppShell>
      <Seo description="Your profile and preferences." noindex path="/settings" title="Settings" />
      <div className="flex flex-col gap-6">
        <header>
          <h1 className="text-2xl font-semibold text-ink">{t("nav.settings")}</h1>
          <p className="mt-1 text-sm text-ink-muted">{t("settings.pageSubtitle")}</p>
        </header>

        {}
        <Card>
          <CardBody className="flex flex-wrap items-center gap-4 sm:gap-5">
            <Avatar name={user.displayName} size="xl" url={user.avatarUrl} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-semibold text-ink">{user.displayName}</p>
              <p className="truncate text-sm text-ink-muted" dir="ltr">
                {user.email}
              </p>
              {since === "" ? null : (
                <p className="mt-1 text-xs text-ink-muted">
                  {t("settings.memberSince", { date: since })}
                </p>
              )}
            </div>
          </CardBody>
        </Card>

        {}
        <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
          <section aria-labelledby="settings-account" className="flex flex-col gap-6">
            <h2
              className="text-xs font-semibold tracking-wide text-ink-muted uppercase"
              id="settings-account"
            >
              {t("settings.groupAccount")}
            </h2>

            <Card>
              <CardHeader description={t("auth.nameIsShown")} title={t("auth.displayName")} />
              <CardBody>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(event) => {
                    void saveProfile(event);
                  }}
                >
                  <TextField
                    label={t("auth.displayName")}
                    maxLength={60}
                    onChange={(event) => {
                      setDisplayName(event.target.value);
                    }}
                    required
                    value={displayName}
                  />
                  <Button
                    className="self-start"
                    disabled={displayName.trim() === user.displayName}
                    loading={savingProfile}
                    type="submit"
                  >
                    {t("common.save")}
                  </Button>
                </form>
              </CardBody>
            </Card>

            <Card>
              <CardHeader description={t("auth.passwordSection")} title={t("auth.password")} />
              <CardBody>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(event) => {
                    void savePassword(event);
                  }}
                >
                  <TextField
                    autoComplete="current-password"
                    label={t("auth.currentPassword")}
                    onChange={(event) => {
                      setCurrentPassword(event.target.value);
                    }}
                    required
                    type="password"
                    value={currentPassword}
                  />
                  <TextField
                    autoComplete="new-password"
                    hint={t("auth.passwordHint", { count: 10 })}
                    label={t("auth.newPassword")}
                    minLength={10}
                    onChange={(event) => {
                      setNewPassword(event.target.value);
                    }}
                    required
                    type="password"
                    value={newPassword}
                  />
                  <Button
                    className="self-start"
                    disabled={currentPassword === "" || newPassword.length < 10}
                    loading={savingPassword}
                    type="submit"
                  >
                    {t("auth.changePassword")}
                  </Button>
                </form>
              </CardBody>
            </Card>
          </section>

          <section aria-labelledby="settings-audio" className="flex flex-col gap-6">
            <h2
              className="text-xs font-semibold tracking-wide text-ink-muted uppercase"
              id="settings-audio"
            >
              {t("settings.groupAudio")}
            </h2>

            {}
            <Card>
              <CardHeader description={t("sound.settingsBody")} title={t("sound.title")} />
              <CardBody>
                <SoundSettings tone="app" />
              </CardBody>
            </Card>

            {}
            <Card>
              <CardHeader description={t("mic.settingsBody")} title={t("mic.title")} />
              <CardBody>
                <MicSettings tone="app" />
              </CardBody>
            </Card>
          </section>
        </div>
      </div>
    </AppShell>
  );
}
